import React, { useState, useEffect } from "react";
import { List as ListEntity } from "@/entities/all";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Edit2, Check, X } from "lucide-react";
import { useData } from "../DataContext";
import type { List } from "@/types";

interface ListHeaderProps {
  list: List;
}

export default function ListHeader({ list }: ListHeaderProps) {
  const { folders, lists, sections, listItems, runOptimisticUpdate } = useData();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(list.title);

  useEffect(() => {
    setTitle(list.title);
  }, [list.title]);

  const handleSave = async () => {
    if (title.trim() && title !== list.title) {
      const originalState = { lists: [...lists] }; 
      const task = async () => ListEntity.update(list.id, { title: title.trim() });
      const newState = {
        folders: [...folders],
        lists: lists.map(l => 
          l.id === list.id ? { ...l, title: title.trim() } : l
        ),
        sections: [...sections],
        listItems: [...listItems]
      };
      runOptimisticUpdate(task, { update: newState, originalState: { folders, ...originalState, sections, listItems } });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTitle(list.title);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 w-full">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="text-lg sm:text-2xl font-bold border-0 px-0 h-auto focus-visible:ring-0 bg-transparent min-w-0 flex-1"
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }}
          autoFocus
        />
        <Button size="sm" variant="ghost" onClick={handleSave} className="flex-shrink-0">
          <Check className="w-4 h-4 text-green-600" />
        </Button>
        <Button size="sm" variant="ghost" onClick={handleCancel} className="flex-shrink-0">
          <X className="w-4 h-4 text-red-600" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 group w-full">
      <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-slate-900 break-words min-w-0 flex-1">
        {list.title}
      </h1>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setIsEditing(true)}
        className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0"
      >
        <Edit2 className="w-4 h-4" />
      </Button>
    </div>
  );
}
